/* eslint-disable no-restricted-syntax -- 도메인 산재 색상 (1~2건/색) — 디자인 시스템 토큰화 효율 < 50%, 별도 디자인 정리 plan으로 분리 */
import { style, globalStyle, keyframes } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { reducedMotion, highContrast, mobile, reducedTransparency } from '../../theme/media';
import { highContrastGlassFallback } from '../../styles/glass.css';
import {
  errorShake,
  errorFadeIn,
  glitchExit,
  glitchEnter,
  scanWipe,
  frameGlitchIntense,
  chromaGlitch,
  successReveal,
} from '../../keyframes/auth.css';

/**
 * AuthScene - 인증 페이지(Login, Register) 공유 스타일
 * FSD shared 레이어: pages 간 크로스 임포트 방지
 */

// ============================================
// SCENE (전체 배경)
// ============================================

export const authScene = style({
  position: 'relative',
  minHeight: '100dvh',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  // 세로 짧은 화면에서 스텝이 뷰포트를 넘으면 잘리지 않고 스크롤되게 — 가로는 여전히 클립
  // (장식 오브의 음수 오프셋이 가로 스크롤을 만들지 않도록).
  overflowX: 'hidden',
  overflowY: 'auto',
  // 노치/펀치홀 안전영역 확보. 하단은 keyboardInset 인라인 paddingBottom 이 담당한다.
  paddingTop: 'env(safe-area-inset-top)',
  paddingLeft: 'env(safe-area-inset-left)',
  paddingRight: 'env(safe-area-inset-right)',
  background: `${vars.opacityScale.blackOverlay.solid}`,
  perspective: '1500px',
  '@media': {
    [mobile]: {
      perspective: 'none',
    },
  },
});

globalStyle(`${authScene}`, {
  background: `radial-gradient(ellipse at 50% 30%, #0a1628 0%, #050a14 50%, ${vars.opacityScale.blackOverlay.solid} 100%)`,
});

globalStyle(`${authScene}::before`, {
  content: '""',
  position: 'absolute',
  inset: '0',
  background: `
    radial-gradient(ellipse at 20% 20%, ${vars.surface.highlight.light} 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, ${vars.bright.secondary.faint} 0%, transparent 50%),
    radial-gradient(ellipse at 50% 50%, ${vars.surface.highlight.faint} 0%, transparent 60%),
    radial-gradient(ellipse at 60% 30%, ${vars.bright.warm.faint} 0%, transparent 40%)
  `,
  opacity: '0.4',
});

globalStyle(`${authScene}::after`, {
  content: '""',
  position: 'absolute',
  inset: '0',
  pointerEvents: 'none',
  opacity: '0',
});

// ============================================
// SCENE OVERLAYS
// ============================================

export const vignetteOverlay = style({
  position: 'absolute',
  inset: '0',
  pointerEvents: 'none',
  boxShadow: `inset 0 0 200px 50px ${vars.shadow.vignette}`,
  zIndex: vars.elevation.interactive,
  transition: `opacity ${vars.animation.duration.slower}`,
  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
});

export const globalScan = style({
  display: 'none',
});

// ============================================
// AMBIENT ORBS
// ============================================

export const ambientOrb = style({
  position: 'absolute',
  borderRadius: '50%',
  filter: 'blur(80px)',
  pointerEvents: 'none',
  zIndex: vars.elevation.decorative,
  transition: `opacity ${vars.animation.duration.xslow}`,
  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
    [mobile]: {
      filter: 'blur(40px)',
      transform: 'scale(0.6)',
    },
  },
});

export const ambientOrbBlue = style({
  width: '300px',
  height: '300px',
  background: vars.surface.highlight.medium,
  top: '-100px',
  left: '-100px',
});

export const ambientOrbCyan = style({
  width: '250px',
  height: '250px',
  background: vars.bright.secondary.light,
  bottom: '-80px',
  right: '-80px',
});

export const ambientOrbWarm = style({
  width: '200px',
  height: '200px',
  background: vars.bright.warm.faint,
  top: '30%',
  right: '-60px',
});

// 장식 오브 클립 레이어 — 씬이 스크롤 컨테이너가 되면 음수 오프셋 오브(bottom/right -Npx)가
// 스크롤 영역을 늘려 팬텀 스크롤바를 만든다. inset:0 로 가둬 스크롤 영역 확장을 막고 클립만 한다.
export const sceneDecor = style({
  position: 'absolute',
  inset: '0',
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: vars.elevation.decorative,
});

// ============================================
// CENTER COLUMN (panel + above-panel slot)
// ============================================

export const authCenterColumn = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  margin: 'auto',
  zIndex: vars.elevation.interactive,
  // 폰 — 폭을 주지 않으면 패널의 width 100% 가 기준을 잃고 max 420px 로 펴져 화면 밖으로 잘린다
  '@media': {
    [mobile]: { width: '100%', boxSizing: 'border-box', padding: '0 12px' },
  },
});

// ============================================
// PANEL & CONTENT
// ============================================

export const authPanel = style({
  position: 'relative',
  width: '480px',
  // height 고정 제거 — size-agnostic 프레임이 콘텐츠에 맞춰 늘어난다.
  height: 'auto',
  minHeight: '520px', // 짧은 스텝도 사이버 패널로 읽히게 하는 floor (SB 에서 튜닝)
  display: 'flex',
  flexDirection: 'column',
  transformStyle: 'preserve-3d',
  zIndex: vars.elevation.interactive,
  '@media': {
    [mobile]: {
      width: '100%',
      maxWidth: '420px',
      height: 'auto',
      transformStyle: 'flat',
      padding: 0,
    },
    [reducedMotion]: {
      transformStyle: 'flat',
    },
  },
});

export const authContentContainer = style({
  position: 'relative',
  flex: '1',
  minHeight: '0',
  margin: '20px 24px',
  display: 'flex',
  flexDirection: 'column',
  contain: 'paint',
  background: `radial-gradient(ellipse at 50% 40%, ${vars.opacityScale.whiteOverlay.soft} 0%, transparent 40%, ${vars.opacityScale.blackOverlay.light} 100%), ${vars.surface.liquidGlass.tint.accent}, linear-gradient(
    145deg,
    ${vars.surface.highlight.strong} 0%,
    ${vars.surface.highlight.medium} 25%,
    ${vars.surface.highlight.light} 50%,
    ${vars.bright.secondary.faint} 75%,
    ${vars.bright.secondary.medium} 100%
  )`,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.heavy,
  WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.heavy,
  boxShadow: `inset 0 0 24px ${vars.opacityScale.blackOverlay.mediumStrong}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.medium}, inset 0 -2px 8px ${vars.opacityScale.blackOverlay.firm}, 0 0 8px ${vars.bright.primary.glow}`,
  clipPath:
    'polygon(28px 0, calc(100% - 28px) 0, 100% 28px, 100% calc(100% - 28px), calc(100% - 28px) 100%, 28px 100%, 0 calc(100% - 28px), 0 28px)',
  overflow: 'hidden',
  '@media': {
    // 폰 — 틀 안쪽 여백(패널 · 본문 · 폼 합 68px)을 줄여 입력칸 폭을 되찾는다
    [mobile]: { margin: '16px 12px' },
    [reducedTransparency]: {
      backdropFilter: vars.surface.liquidGlass.backdropFilter.none,
      WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.none,
      background: vars.surface.background.opaque,
    },
    [highContrast]: {
      ...highContrastGlassFallback,
      clipPath: 'none',
    },
  },
});

globalStyle(`${authContentContainer}::before`, {
  content: '""',
  position: 'absolute',
  inset: '2px',
  background: `linear-gradient(180deg, ${vars.surface.background.primary} 0%, ${vars.surface.background.opaque} 100%)`,
  clipPath:
    'polygon(26px 0, calc(100% - 26px) 0, 100% 26px, 100% calc(100% - 26px), calc(100% - 26px) 100%, 26px 100%, 0 calc(100% - 26px), 0 26px)',
  zIndex: -1,
});

export const innerGlow = style({
  position: 'absolute',
  inset: '0',
  // 광원은 SystemFrame 과 같은 위쪽 가운데 — 위에서 빛이 패널 안으로 번지고 아래는 그늘에 든다.
  background: `
    radial-gradient(ellipse 70% 32% at 50% 0%, ${vars.bright.secondary.light} 0%, transparent 70%),
    linear-gradient(180deg, ${vars.surface.highlight.light} 0%, transparent 30%),
    linear-gradient(0deg, ${vars.opacityScale.blackOverlay.firm} 0%, transparent 35%)
  `,
  borderRadius: '2px',
  pointerEvents: 'none',
});

export const noiseOverlay = style({
  position: 'absolute',
  inset: '0',
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.02'/%3E%3C/svg%3E")`,
  pointerEvents: 'none',
  borderRadius: '2px',
  mixBlendMode: 'overlay',
  zIndex: vars.elevation.scene,
});

// ============================================
// SEGMENTS — 패널 안 세 구간(배너 · 머리 · 입력)의 밝기 계단
// 광원(SystemFrame 과 같은 위쪽 가운데)에서 멀어질수록 한 단씩 어두워진다.
// 구간 사이는 홈으로 갈라진다: 윗 구간 아래의 어두운 줄 + 아랫 구간 위의 밝은 줄.
// ============================================

export const segmentBanner = style({
  position: 'relative',
  borderBottom: `1px solid ${vars.opacityScale.blackOverlay.deep}`,
  '::after': {
    content: '""',
    position: 'absolute',
    inset: '0',
    background: `linear-gradient(180deg, ${vars.opacityScale.whiteOverlay.light} 0%, transparent 60%)`,
    pointerEvents: 'none',
  },
});

export const segmentHeader = style({
  position: 'relative',
  borderTop: `1px solid ${vars.opacityScale.whiteOverlay.medium}`,
  borderBottom: `1px solid ${vars.opacityScale.blackOverlay.deep}`,
  background: `radial-gradient(ellipse 80% 55% at 50% 0%, ${vars.surface.highlight.medium} 0%, transparent 70%), ${vars.opacityScale.whiteOverlay.faint}`,
});

export const segmentForm = style({
  borderTop: `1px solid ${vars.opacityScale.whiteOverlay.light}`,
  background: `linear-gradient(0deg, ${vars.opacityScale.blackOverlay.firm} 0%, ${vars.opacityScale.blackOverlay.light} 100%)`,
});

// ============================================
// FORM AREA
// ============================================

export const authFormArea = style({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '24px 28px',
  // 내부 스크롤 제거 — 콘텐츠가 패널 높이를 결정한다 (minHeight:0 + overflowY:auto 제거).
  overflowX: 'hidden',
  '@media': {
    [mobile]: { padding: '16px 16px 20px' },
  },
});

export const formStyles = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const buttonGroup = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// ============================================
// ERROR FEEDBACK
// ============================================

export const errorBox = style({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '14px 16px',
  background: `linear-gradient(90deg, ${vars.bright.status.errorFaint} 0%, ${vars.bright.overlay.light} 100%)`,
  border: `1px solid ${vars.bright.status.errorBorder}`,
  borderLeft: `3px solid ${vars.bright.status.error}`,
  borderRadius: '4px',
  marginBottom: '16px',
  position: 'relative',
  overflow: 'hidden',
  flexShrink: 0,
  animation: `${errorFadeIn} ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}, ${errorShake} ${vars.animation.duration.slow} ${vars.animation.easing.easeInOut}`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
    },
  },
});

export const errorIcon = style({
  color: vars.bright.status.error,
  flexShrink: 0,
  marginTop: '1px',
  filter: `drop-shadow(0 0 6px ${vars.bright.status.error})`,
});

export const errorText = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.xs,
  color: vars.bright.status.error,
  margin: '0',
  textShadow: `0 0 5px ${vars.bright.status.errorGlow}`,
});

// ============================================
// STEP TRANSITIONS
// ============================================

export const stepContent = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  flex: 1,
});

export const stepExiting = style({
  animation: `${glitchExit} ${vars.animation.duration.normal} ${vars.animation.easing.easeIn} forwards`,
  pointerEvents: 'none',
  '@media': {
    [reducedMotion]: { animation: 'none', opacity: 0 },
  },
});

export const stepEntering = style({
  animation: `${glitchEnter} ${vars.animation.duration.normal} ${vars.animation.easing.easeOut} forwards`,
  '@media': {
    [reducedMotion]: { animation: 'none', opacity: 1 },
  },
});

export const transitionScanWipe = style({
  position: 'absolute',
  inset: '0',
  background: `linear-gradient(180deg,
    transparent 0%,
    ${vars.bright.secondary.light} 45%,
    ${vars.bright.glow.cyan} 50%,
    ${vars.bright.secondary.light} 55%,
    transparent 100%
  )`,
  animation: `${scanWipe} ${vars.animation.duration.normal} linear forwards`,
  pointerEvents: 'none',
  zIndex: vars.elevation.interactive,
  '@media': {
    [reducedMotion]: { animation: 'none', display: 'none' },
  },
});

export const systemFrameGlitch = style({
  animation: `${frameGlitchIntense} ${vars.animation.duration.normal} linear`,
  '@media': {
    [reducedMotion]: { animation: 'none' },
  },
});

// ============================================
// STEP FORM CONTENT
// ============================================

export const stepFormContent = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

// ============================================
// SUCCESS SCREEN
// ============================================

export const successContainer = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: vars.spacing.gap.lg,
  flex: 1,
  padding: '16px 0',
  animation: `${successReveal} ${vars.animation.duration.slower} ${vars.animation.easing.easeOut} forwards`,
  '@media': {
    [reducedMotion]: { animation: 'none' },
  },
});

export const successIcon = style({
  width: '56px',
  height: '56px',
  color: vars.bright.status.success,
  filter: `drop-shadow(0 0 8px ${vars.bright.status.successGlow}) drop-shadow(0 0 16px ${vars.bright.status.successFaint})`,
});

export const successTitle = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.md,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.bright.status.success,
  letterSpacing: vars.typography.letterSpacing.display,
  textShadow: `0 0 10px ${vars.bright.status.successGlowStrong}, 0 0 20px ${vars.bright.status.successFaint}`,
  animation: `${chromaGlitch} ${vars.animation.duration.medium} linear`,
  '@media': {
    [reducedMotion]: { animation: 'none' },
  },
});

export const successMessage = style({
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.subtitle,
  textAlign: 'center',
  letterSpacing: vars.typography.letterSpacing.wide,
  lineHeight: vars.typography.lineHeight.normal,
});

// ============================================
// SCENE EXIT (auth → main warp transition)
// ============================================

const panelWarpOut = keyframes({
  '0%': { opacity: '1', filter: 'brightness(1)' },
  '50%': { opacity: '0.5', filter: 'brightness(1.3)' },
  '100%': { opacity: '0', filter: 'brightness(1.8)' },
});

const orbFlare = keyframes({
  '0%': { opacity: '1', filter: 'blur(80px)' },
  '50%': { opacity: '0.5', filter: 'blur(120px)' },
  '100%': { opacity: '0', filter: 'blur(160px)' },
});

const sceneWarpOut = keyframes({
  '0%': { opacity: '1' },
  '50%': { opacity: '1' },
  '100%': { opacity: '0' },
});

export const authPanelExiting = style({
  animation: `${panelWarpOut} ${vars.animation.duration.authWarp} ${vars.animation.easing.easeIn} forwards`,
  '@media': { [reducedMotion]: { animation: 'none', opacity: '0' } },
});

export const authOrbExiting = style({
  animation: `${orbFlare} ${vars.animation.duration.slow} ${vars.animation.easing.easeOut} forwards`,
  '@media': { [reducedMotion]: { animation: 'none', opacity: '0' } },
});

export const authSceneExiting = style({
  animation: `${sceneWarpOut} ${vars.animation.duration.slower} ${vars.animation.easing.easeIn} forwards`,
  '@media': { [reducedMotion]: { animation: 'none', opacity: '0' } },
});

const gridFlare = keyframes({
  '0%': { filter: 'brightness(1)' },
  '35%': { filter: 'brightness(2.5)' },
  '100%': { filter: 'brightness(0.5)', opacity: '0' },
});

export const authGridFlare = style({
  animation: `${gridFlare} ${vars.animation.duration.slower} ${vars.animation.easing.easeOut} forwards`,
  '@media': { [reducedMotion]: { animation: 'none' } },
});
